<template>
  <div class="subscriptions">
    <div class="container view" v-if="pricing && subscriptions">
      <Card class="subscriptions__card">
        <template #header>
          <h1 class="subscriptions__title">{{ $t('subscriptionOverview') }}</h1>
          <span class="subscriptions__subtitle">{{ $t('pricesInExludingVat') }}</span>
          <Alert class="subscriptions__alert" iconPlacement="left" :icon="['fal', 'info-circle']" theme="primary">
            {{ $t('subscriptionOverviewAlert', { x: '' }) }}
            <a :href="`mailto:${email}`" target="_blank">{{ email }}</a>
          </Alert>
        </template>

        <template #default>
          <h2>{{ $t('basicPackage') }}</h2>
          <Card theme="border" class="subscriptions__row-card">
            <template #default>
              <Row v-for="(item, index) in standard" :row="item" :key="`${index}-standard`">
                <!-- <template #trigger="{ trigger }">{{ $t('readMore') }}</template> -->
              </Row>

              <Row v-for="(item, index) in unitBanks" :row="item" :key="`${index}-unit-bank`">
                <template #trigger="{ open }">
                  <FontAwesomeIcon :icon="['fas', open() ? 'chevron-up' : 'chevron-down']" />
                </template>
                <template #content>
                  <DataTable
                    :row-wrapper="
                      (item, row) => {
                        return { component: 'div' }
                      }
                    "
                    :items="item.banks"
                    :columns="[
                      { id: 'label', label: $tc('product', 1) },
                      { id: 'used', label: $t('used') },
                      { id: 'remaining', label: $t('remaining') },
                      { id: 'activate', label: $t('period') },
                    ]"
                  >
                    <template #secondary="{ item, rowIndex }"></template>
                  </DataTable>

                  <DataTable
                    class="subscription-data-table--counts"
                    :style="{ '--data-table-count': item.banks.length }"
                    :row-wrapper="
                      (item, row) => {
                        return { component: 'div' }
                      }
                    "
                    :items="item.projects"
                    :columns="item.projectColumns"
                  >
                    <template #secondary="{ item, rowIndex }"></template>
                  </DataTable>

                  <DataTable
                    class="subscription-data-table--counts"
                    :style="{ '--data-table-count': item.banks.length }"
                    :row-wrapper="
                      (item, row) => {
                        return { component: 'div' }
                      }
                    "
                    :items="item.history"
                    :columns="item.historyColumns"
                  >
                    <template #secondary="{ item, rowIndex }"></template>
                  </DataTable>
                </template>
              </Row>
            </template>
          </Card>

          <template v-if="projects.length">
            <h2>{{ $tc('project', 2) }}</h2>
            <Card theme="border" class="subscriptions__row-card">
              <template #default>
                <Row v-for="(projectItem, index) in projects" :row="projectItem" :key="index">
                  <template #trigger="{ open }">
                    <FontAwesomeIcon :icon="['fas', open() ? 'chevron-up' : 'chevron-down']" />
                  </template>
                  <template #content>
                    <DataTable
                      :row-wrapper="
                        (item, row) => {
                          return item.addon === 'project'
                            ? { component: 'div' }
                            : {
                                component: 'div',
                                listeners: { click: openModal.bind(null, item, projectItem.id) },
                                props: {
                                  style: 'cursor: pointer',
                                },
                              }
                        }
                      "
                      :items="projectItem.defaultAddons"
                      :columns="[
                        { id: 'label', label: $t('standardModules') },
                        { id: 'status', label: $t('status') },
                        { id: 'activate', label: $t('activationDate') },
                        { id: 'price', label: $t('price'), align: 'end' },
                      ]"
                    >
                      <template #secondary="{ item, rowIndex }">
                        <!-- <ButtonComponent
                        v-if="item.status !== 'active'"
                        :icon="['fal', 'external-link']"
                        :label="$t('readMore')"
                      /> -->

                        <Modal
                          :ref="`${item.addon}:${projectItem.id}`"
                          key="cancel"
                          v-if="item.status === 'active' && item.addon !== 'project'"
                          :title="$t(`addon:${item.addon}:cancel`)"
                          :description="$t(`addon:${item.addon}:cancel:description`)"
                          :triggerLabel="$t('terminateAgreement')"
                          :items="[
                            {
                              value: 'completedProject',
                              label: $t('completedProject'),
                            },
                            {
                              value: 'economy',
                              label: $t('economy'),
                            },
                            {
                              value: 'systemFailure',
                              label: $t('systemFailure'),
                              textField: true,
                              textFieldLabel: $t('explainImprovements'),
                              required: true,
                            },
                            {
                              value: 'other',
                              label: $t('other'),
                              textField: true,
                              textFieldLabel: $t('canYouExplain'),
                            },
                          ]"
                          @input="
                            reason =>
                              addonHandler({
                                addon: item.addon,
                                reason,
                                onModel: 'Project',
                                id: projectItem.id,
                                state: false,
                              })
                          "
                          :promise="promise"
                        />

                        <Modal
                          :ref="`${item.addon}:${projectItem.id}`"
                          key="activate"
                          align="center"
                          :thumbnail="`/images/subscription/activate/${item.addon}.png`"
                          :title="$t(`addon:${item.addon}:activate`)"
                          :description="$t(`addon:${item.addon}:activate:description`)"
                          v-if="item.status !== 'active'"
                          :triggerLabel="$t('activate')"
                          @input="
                            () =>
                              addonHandler({ addon: item.addon, id: projectItem.id, onModel: 'Project', state: true })
                          "
                          :promise="promise"
                        />

                        <ButtonComponent
                          v-if="$store.getters['isSuperadmin'] && ['active', 'cancelled'].includes(item.status)"
                          tag="router-link"
                          :label="$t('changeX', { x: '' })"
                          :to="{
                            name: 'subscription-edit',
                            params: { id: item.id },
                            query: { redirect: redirectUrl },
                          }"
                        />
                      </template>
                    </DataTable>

                    <DataTable
                      :items="projectItem.extraAddons"
                      :columns="[
                        { id: 'label', label: $t('additionalProducts') },
                        { id: 'status', label: $t('status') },
                        { id: 'activate', label: $t('activationDate') },
                        { id: 'price', label: $t('price'), align: 'end' },
                      ]"
                    >
                      <template #secondary="{ item, rowIndex }">
                        <ButtonComponent
                          v-if="item.addon.includes('integration')"
                          :label="item.status !== 'active' ? $t('activate') : $t('terminateAgreement')"
                          :icon="['fal', 'long-arrow-right']"
                          tag="router-link"
                          :to="{
                            name: 'project-edit',
                            params: {
                              id: projectItem.id,
                              step: 'integrations',
                            },
                          }"
                        />
                      </template>
                    </DataTable>
                  </template>
                </Row>
              </template>
            </Card>
          </template>

          <template v-if="pages.length">
            <h2>{{ $tc('page', 2) }}</h2>
            <Card theme="border" class="subscriptions__row-card">
              <template #default>
                <Row v-for="(pageItem, index) in pages" :row="pageItem" :key="index">
                  <template #trigger="{ open }">
                    <FontAwesomeIcon :icon="['fas', open() ? 'chevron-up' : 'chevron-down']" />
                  </template>
                  <template #content>
                    <DataTable
                      :items="pageItem.addons"
                      :columns="[
                        { id: 'label', label: $tc('product', 1) },
                        { id: 'status', label: $t('status') },
                        { id: 'activate', label: $t('activationDate') },
                        { id: 'price', label: $t('price'), align: 'end' },
                      ]"
                    >
                      <template #secondary="{ item }">
                        <ButtonComponent
                          tag="router-link"
                          :label="$t('changeX', { x: '' })"
                          :to="{
                            name: 'subscription-edit',
                            params: { id: item.id },
                            query: { redirect: redirectUrl },
                          }"
                        />
                      </template>
                    </DataTable>
                  </template>
                </Row>
              </template>
            </Card>
          </template>

          <template v-if="rest.length">
            <h2>{{ $t('other') }}</h2>
            <Card theme="border" class="subscriptions__row-card">
              <template #default>
                <Row v-for="(item, index) in rest" :row="item" :key="index"> </Row>
              </template>
            </Card>
          </template>
        </template>

        <template #footer>
          <div class="subscriptions-total-price__label-wrapper">
            <span class="subscriptions-total-price__label">{{
              isAgreementPriceMissing ? $t('additionsBeyondAgreementPerMonth') : $t('collectedMonthPrice')
            }}</span>
            <span class="subscriptions-total-price__sublabel">{{ $t('moduleInvoiceDatePrice') }}</span>
          </div>
          <div class="subscriptions-total-price__value-wrapper">
            <span class="subscriptions-total-price__value">kr {{ totalPrice.toLocaleString('nb-no') }}</span>
            <span class="subscriptions-total-price__suffix"> /mnd</span>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script>
import Card from '@/components/Card'
import { AlertComponent as Alert } from 'vue-elder-alert'
import moment from 'moment'

import Row from '@/components/Subscription/Row'
import DataTable from '@/components/Subscription/DataTable'
import Modal from '@/components/Subscription/Modal'

import SubscriptionApi from '@/api/Subscription'
import ProjectApi from '@/api/Project'
import PageApi from '@/api/Page'

function removeDuplicates(arr) {
  return [...new Set(arr)]
}

function getPrice(prices, types = ['monthly']) {
  return prices.reduce((result, price) => {
    if (types.includes(price.type)) result += price.value
    return result
  }, 0)
}

export default {
  data() {
    return {
      pricing: null,
      subscriptions: null,
      promise: null,
      unitBank: {
        units: { total: 0, remaining: 0, active: false, startDate: null },
        unitsPlot: { total: 0, remaining: 0, active: false, startDate: null },
        shops: { total: 0, remaining: 0, active: false, startDate: null },
      },
    }
  },
  computed: {
    email() {
      return 'faktura@kvass.no'
    },

    redirectUrl() {
      return window.location.href
    },

    totalPrice() {
      return [...this.standard, ...this.unitBanks, ...this.pages, ...this.projects, ...this.rest].reduce(
        (total, item) => (total += item.price),
        0,
      )
    },

    isAgreementPriceMissing() {
      if (this.banks.some(bank => bank.price)) return false
      return Boolean(this.banks.length) || this.subscriptions.some(s => this.isCustomPriced(s.item))
    },

    standard() {
      return ['app', 'custom-contract', 'api'].map(key => {
        const item = this.subscriptions.find(s => s.item === key)

        if (!item) return { label: this.$t(`addon:${key}`), price: 0, priceLabel: this.$t('notActive') }

        return {
          label: this.$t(`addon:${key}`),
          ...this.getRowPrice([item]),
        }
      })
    },

    banks() {
      const labels = {
        units: this.$tc('unit', 2),
        unitsPlot: this.$tc('propertyType:plot', 2),
        shops: this.$t('subscription:project:shop:label'),
      }

      return Object.entries(this.unitBank)
        .map(([id, bank]) => ({ ...bank, id, label: labels[id] }))
        .filter(bank => bank.active)
    },

    unitBanks() {
      if (!this.banks.length) return []

      const projects = {}
      this.banks.forEach(bank => {
        bank.projects.forEach(project => {
          projects[project._id] = { ...projects[project._id], label: project.name, [bank.id]: project.count }
        })
      })

      const periods = {}
      this.banks.forEach(bank => {
        bank.history.forEach(period => {
          periods[period.windowStart] = {
            ...periods[period.windowStart],
            start: period.windowStart,
            end: moment(period.windowEnd).subtract(1, 'day'),
            [bank.id]: period.used,
          }
        })
      })

      const bankColumns = this.banks.map(bank => ({ id: bank.id, label: bank.label }))

      const price = this.banks.reduce((total, bank) => total + bank.price, 0)

      const getRemainingLabel = bank => {
        const label = bank.label.toLowerCase()
        if (bank.remaining < 0) return this.$t('xOverLimit', { x: -bank.remaining, label })
        return this.$t('xOfYRemaining', { x: bank.remaining, y: bank.total + bank.adjusted, label })
      }

      return [
        {
          id: 'unit-bank',
          label: this.$t('unitBank'),
          sublabel: this.banks.map(getRemainingLabel).join(' · '),
          price,
          priceLabel: price ? null : this.$t('customPricingLabel'),
          banks: this.banks.map(bank => ({
            ...bank,
            start: bank.windowStart,
            end: moment(bank.windowEnd).subtract(1, 'day'),
          })),
          projects: Object.values(projects),
          projectColumns: [{ id: 'label', label: this.$tc('project', 1) }, ...bankColumns],
          history: Object.values(periods).sort((a, b) => b.start.localeCompare(a.start)),
          historyColumns: [{ id: 'activate', label: this.$t('previousPeriods') }, ...bankColumns],
        },
      ]
    },

    pages() {
      const pageSubscriptions = this.subscriptions.filter(s => this.$path('reference.onModel', s) === 'Page')

      const ids = removeDuplicates(pageSubscriptions.map(s => s.metadata.source._id))

      return ids.map(id => {
        const items = pageSubscriptions.filter(s => s.metadata.source._id === id)

        return {
          id,
          label: items[0].metadata.source.name,
          ...this.getRowPrice(items),
          addons: items.map(item => this.getAddonRow(item)),
        }
      })
    },

    projects() {
      const pItems = this.pricing.items.find(i => i.id === 'project')

      const addons = [...pItems.addons.map(i => i.id), pItems.id]

      const defaultAddons = ['project', 'presentation', 'bid', 'purchase', 'shop', 'shop-purchase', 'complaint']

      const exludedAddons = ['flatfinder', 'residential-account']

      const extraAddons = addons.filter(add => !defaultAddons.includes(add) && !exludedAddons.includes(add))

      const projectSubscriptions = this.subscriptions.filter(s => this.$path('reference.onModel', s) === 'Project')

      const ids = removeDuplicates(projectSubscriptions.map(s => this.$path('reference.ref._id', s))).filter(Boolean)

      return ids.map(id => {
        const items = projectSubscriptions.filter(s => this.$path('reference.ref._id', s) === id)

        const limit = this.$path('conditions.units.gte', items[0].price[0]) || 0

        return {
          id: id,
          label: items[0].metadata.source.name,
          badge: limit ? `${limit}+ ${this.$tc('unit', 2).toLowerCase()}` : null,
          ...this.getRowPrice(items),
          defaultAddons: this.getTableData(defaultAddons, items, limit),
          extraAddons: this.getTableData(extraAddons, items, limit),
        }
      })
    },

    rest() {
      return ['ads', 'integration-mailchimp-global']
        .map(key => {
          const item = this.subscriptions.find(s => s.item === key)

          if (!item) return

          return {
            label: this.$t(`addon:${key}`),
            ...this.getRowPrice([item]),
          }
        })
        .filter(Boolean)
    },
  },
  methods: {
    openModal(item, projectId) {
      this.$refs[`${item.addon}:${projectId}`][0].open()
    },
    isCustomPriced(item) {
      return this.$store.state.enums.CustomSubscriptionPricing.includes(item)
    },
    getRowPrice(items) {
      const price = getPrice(
        items
          .filter(i => !this.isCustomPriced(i.item))
          .map(i => i.price)
          .flat(),
      )

      if (price) return { price }

      return {
        price,
        priceLabel: this.$t(items.some(i => this.isCustomPriced(i.item)) ? 'customPricingLabel' : 'included'),
      }
    },
    getAddonRow(subscription) {
      return {
        id: subscription._id,
        addon: subscription.item,
        status: subscription.end ? 'cancelled' : 'active',
        start: subscription.start,
        end: subscription.end,
        priceMonthly: getPrice(subscription.price),
        priceFixed: getPrice(subscription.price, ['fixed', 'establishment']),
        priceLabel: this.isCustomPriced(subscription.item) ? this.$t('customPricingLabel') : null,
      }
    },
    getTableData(addons, items, limit) {
      return addons
        .map(addon => {
          const addonItem = items.find(i => [`project:${addon}`, addon].includes(i.item))

          if (addonItem) return this.getAddonRow(addonItem)

          const step = (this.pricing.steps || []).find(step => {
            const cond = step.condition
            const gteCheck = cond.gte !== undefined ? limit >= cond.gte : true
            const lteCheck = cond.lte !== undefined ? limit <= cond.lte : true
            return gteCheck && lteCheck
          })

          const getProjectItem = items => items.find(i => i.id === 'project')

          const pItem = step ? getProjectItem(step.items) : getProjectItem(this.pricing.items)

          const defaultItem = addon === pItem.id ? pItem : pItem.addons.find(i => i.id === addon)

          if (!defaultItem) return

          const key = defaultItem.id === 'project' ? defaultItem.id : `project:${defaultItem.id}`

          return {
            addon: key,
            status: 'notActive',
            start: null,
            end: null,
            priceMonthly: getPrice(defaultItem.price),
            priceFixed: getPrice(defaultItem.price, ['fixed', 'establishment']),
            priceLabel: this.isCustomPriced(key) ? this.$t('customPricingLabel') : null,
          }
        })
        .filter(Boolean)
    },

    setData() {
      return Promise.all([
        fetch(`/api/subscription/active`).then(res => res.json()),
        fetch(`/api/pricing`).then(res => res.json()),
        fetch(`/api/subscription/unit-bank`).then(res => res.json()),
      ]).then(values => {
        const [subscriptions, pricing, unitBank] = values

        this.subscriptions = subscriptions.filter(s => !s.demo && !this.$path('reference.ref.demo', s))
        this.pricing = pricing
        this.unitBank = unitBank
      })
    },

    addonHandler(options = {}) {
      const { addon, reason, onModel, id, state } = options

      const getHandler = () => {
        switch (onModel) {
          case 'Project':
            return ProjectApi.addon({ project: id, state, addon, reason })
          case 'Page':
            return PageApi.addon({ page: id, state, addon, reason })
          default:
            return SubscriptionApi.addon({ addon, state, reason })
        }
      }

      return (this.promise = getHandler().then(() => this.setData()))
    },
  },
  created() {
    this.promise = this.setData()
  },
  metaInfo() {
    return {
      title: this.$t('subscriptionOverview'),
    }
  },
  components: {
    Card,
    Alert,
    Row,
    DataTable,
    Modal,
  },
}
</script>

<style lang="scss">
.subscriptions {
  background-color: $background-grey;

  &__card {
    .card__header {
      gap: 0.5rem;
      align-items: start;
      justify-content: left;
      background-color: inherit;
    }

    .card__footer {
      background-color: hsl(var(--border-color-h), var(--border-color-s), calc(var(--border-color-l) + 9%));
      padding-top: var(--card-spacing);
      &-content {
        justify-content: space-between;
      }
    }
  }

  &-total-price {
    &__label {
      &-wrapper {
        display: flex;
        flex-direction: column;
      }
    }
    &__sublabel {
      font-size: 0.8rem;
      opacity: 0.7;
    }
    &__value {
      font-weight: 700;
      font-size: 1.5rem;
      &-wrapper {
        margin-right: calc(100px + var(--card-spacing) + 1rem);
      }
    }
    &__suffix {
      opacity: 0.7;
    }
  }

  &__title {
    width: 100%;
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 0.5rem;
    margin-bottom: 0;
  }

  &__subtitle {
    font-weight: bold;
    opacity: 0.7;
  }

  &__alert {
    svg {
      font-size: 2rem;
    }

    .alert__content {
      border-radius: inherit;
      &:before {
        border-radius: inherit;
      }
    }
  }

  .container {
    max-width: 1050px;
  }

  &__row-card {
    .card__content {
      padding: 0;
      border-radius: inherit;
      overflow: hidden;
    }
  }
}
</style>
